import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

/**
 * Markdown — renders AI chat messages.
 *
 * The mock provider emits **bold**, `inline code`, • bullet lists,
 * 1. numbered lists, and the occasional > blockquote. Real LLM
 * responses use the same conventions. Without this component the
 * user sees raw asterisks and backticks in the chat.
 *
 * Tailwind isn't great at element-typography without `@tailwindcss/typography`,
 * so we style elements directly. Compact spacing — chat bubbles need
 * tight rhythm, not article-grade leading.
 *
 * Links open in a new tab with rel="noopener noreferrer" for safety —
 * the AI can output any URL and we don't want to ship the chat session
 * along with the click.
 */
export function Markdown({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  return (
    <div className={cn("markdown-body", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Tight paragraph spacing; chat bubbles aren't articles.
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          strong: ({ children }) => (
            <strong className="font-semibold">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          // Inline / block code share a style — most mock output is
          // inline (URLs, command lines). Real code blocks are rare
          // but handled with a slightly heavier background.
          code: ({ className, children, ...props }) => {
            // react-markdown distinguishes inline vs block by whether
            // the className is set (block code gets language-* class).
            const isBlock = !!className;
            if (isBlock) {
              return (
                <pre className="my-2 rounded-lg bg-slate-900 text-slate-100 text-xs px-3 py-2 overflow-x-auto">
                  <code className={className} {...props}>
                    {children}
                  </code>
                </pre>
              );
            }
            return (
              <code className="px-1 py-0.5 rounded bg-slate-200/70 text-[0.85em] font-mono">
                {children}
              </code>
            );
          },
          ul: ({ children }) => (
            <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-slate-300 pl-3 my-2 italic text-slate-700">
              {children}
            </blockquote>
          ),
          h1: ({ children }) => (
            <h3 className="text-base font-semibold mt-2 mb-1">{children}</h3>
          ),
          h2: ({ children }) => (
            <h3 className="text-base font-semibold mt-2 mb-1">{children}</h3>
          ),
          h3: ({ children }) => (
            <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              {children}
            </a>
          ),
          hr: () => <hr className="my-3 border-slate-200" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
